import { registerPlugin } from '@capacitor/core';

export interface MagnetometerData {
  x_uT: number;
  y_uT: number;
  z_uT: number;
  magnitude_uT: number;
  accuracy: string;
  timestamp: number;
  samples_in_window?: number;
  min_uT?: number;
  max_uT?: number;
  average_uT?: number;
  peak_to_peak_delta_uT?: number;
  std_dev_uT?: number;
  emf_anomaly_score?: number;
  detection_state?: string;
}

export interface AmbientLightData {
  available: boolean;
  lux: number | null;
  timestamp: number;
}

export interface BatteryDiagnostics {
  level_percent: number;
  temperature_celsius: number | null;
  voltage_mv: number | null;
  status: string;
  is_charging: boolean;
  power_source: string;
  health: string;
  current_ma?: number;
  charge_counter_uah?: number;
}

export interface BastetDiagnosticSnapshot {
  timestamp: number;
  magnetometer: MagnetometerData;
  ambient_light: AmbientLightData;
  ambient_temperature: { available: boolean; celsius: number | null };
  battery: BatteryDiagnostics;
}

export interface NfcTagInfo {
  timestamp: number;
  uid: string;
  uid_raw: string;
  tech_list: string[];
  details: {
    nfc_a?: { atqa: string; sak: string; max_transceive_length: number; timeout_ms: number };
    nfc_b?: { application_data: string; protocol_info: string };
    iso_dep?: { historical_bytes?: string; hi_layer_response?: string; max_transceive_length: number };
    mifare_classic?: { type: string; size_bytes: number; sector_count: number; block_count: number };
    mifare_ultralight?: { type: string; max_transceive_length: number };
    nfc_f?: { system_code?: string; manufacturer?: string };
    nfc_v?: { dsfid: string; response_flags: string };
    ndef?: {
      type: string;
      max_size_bytes: number;
      is_writable: boolean;
      can_make_read_only: boolean;
      records_count: number;
      records: Array<{
        tnf: string;
        type: string;
        id?: string;
        payload_bytes: number;
        payload_hex: string;
        parsed_text?: string;
        parsed_uri?: string;
        ascii_preview?: string;
      }>;
    };
  };
}

export interface BastetStatusResult {
  running: boolean;
  port: number;
  details: {
    service?: string;
    version?: string;
    status?: string;
    uptime_seconds?: number;
    nfc?: {
      supported: boolean;
      enabled: boolean;
      reader_mode_active: boolean;
      last_discovery_timestamp: number;
      has_tag_cached: boolean;
      last_tag_uid?: string;
    };
    sensors?: MagnetometerData;
    usb?: {
      status: string;
      device_count: number;
      baud_rate: number;
    };
  };
}

export interface UsbTxRxResult {
  success: boolean;
  transmitted: string;
  response: string;
  raw_response?: string;
  bytes_sent: number;
  bytes_received: number;
  duration_ms: number;
  framing: string;
  profile: string;
  error?: string;
}

export interface BluetoothDeviceItem {
  name: string;
  address: string;
  type: string;
  bonded: boolean;
  connected?: boolean;
  rssi?: number;
  category?: string;
  source?: 'bonded' | 'ble_scan' | 'dumpsys';
  txPower?: number;
}

export interface BluetoothScanResult {
  success: boolean;
  adapterState: string;
  adapterEnabled: boolean;
  adapterName?: string;
  totalDevices: number;
  devices: BluetoothDeviceItem[];
  durationMs?: number;
  error?: string;
}

export interface WifiAccessPoint {
  ssid: string;
  bssid: string;
  rssi: number;
  frequencyMhz: number;
  channel: number;
  band: string;
  width: string;
  capabilities: string;
  ageSeconds: number;
}

export interface WifiActiveConnection {
  ssid: string;
  bssid?: string;
  rssi?: number;
  linkSpeedMbps?: number;
  frequencyMhz?: number;
  channel?: number;
}

export interface WifiAirspaceResult {
  success: boolean;
  measurementState: string;
  totalAccessPoints: number;
  count24Ghz?: number;
  count5Ghz?: number;
  count6Ghz?: number;
  activeConnection?: WifiActiveConnection;
  recommendations?: {
    best24GhzChannel: number;
    best5GhzChannel: number;
  };
  accessPoints: WifiAccessPoint[];
  durationMs?: number;
  warning?: string;
  error?: string;
}

interface BastetPluginInterface {
  getStatus(): Promise<BastetStatusResult>;
  startService(): Promise<{ success: boolean; message: string }>;
  stopService(): Promise<{ success: boolean; message: string }>;
  getSensorSnapshot(): Promise<BastetDiagnosticSnapshot>;
  readMagnetometer(): Promise<MagnetometerData>;
  pollNfc(options?: { timeoutMs?: number }): Promise<{ tagFound: boolean; tag?: NfcTagInfo; message?: string }>;
  serialTxRx(options: {
    port?: number;
    payload: string;
    timeoutMs?: number;
    baudRate?: number;
    framing?: 'raw' | 'line' | 'json_lines' | 'cobs';
    profile?: 'generic' | 'esp32_console' | 'logic_sniffer' | 'rp2040_picoprobe' | 'bus_pirate';
  }): Promise<UsbTxRxResult>;
  listUsbDevices(): Promise<{ devices: any[] }>;
  scanBluetooth(options?: { timeoutMs?: number }): Promise<BluetoothScanResult>;
  scanWifi(): Promise<WifiAirspaceResult>;
  getCellularInfo(): Promise<any>;
  optimizeScanThrottling(): Promise<{ success: boolean }>;
  /** Generic gateway to every Bastet JSON-RPC method (doctor.*, net.*, usb.*, ...). */
  rpc(options: { method: string; params?: Record<string, any> }): Promise<{ result: any }>;
}

export const BastetNative = registerPlugin<BastetPluginInterface>('Bastet');

export function isBastetNativeSupported(): boolean {
  return typeof (window as any).Capacitor !== 'undefined' && (window as any).Capacitor.isPluginAvailable('Bastet');
}

/**
 * Retrieves the overall status of Project Bastet Sovereign HAL daemon
 */
export async function getBastetStatus(): Promise<BastetStatusResult> {
  if (isBastetNativeSupported()) {
    try {
      return await BastetNative.getStatus();
    } catch (err: any) {
      console.warn('[BastetBridge] Plugin getStatus error:', err);
    }
  }

  // Loopback HTTP fallback (:8777)
  try {
    const res = await fetch('http://127.0.0.1:8777/status', {
      method: 'GET',
      headers: { Accept: 'application/json' },
      signal: AbortSignal.timeout(1000)
    });
    if (res.ok) {
      const data = await res.json();
      return {
        running: true,
        port: 8777,
        details: data
      };
    }
  } catch {}

  // Web preview mock
  return {
    running: false,
    port: 8777,
    details: {
      service: 'Project Bastet Sovereign Diagnostic Bridge (Web Simulation)',
      version: '1.0.0',
      status: 'WEB_PREVIEW',
      uptime_seconds: 0,
      nfc: {
        supported: false,
        enabled: false,
        reader_mode_active: false,
        last_discovery_timestamp: 0,
        has_tag_cached: false
      },
      sensors: {
        x_uT: 14.5,
        y_uT: -38.2,
        z_uT: -22.1,
        magnitude_uT: 46.4,
        accuracy: 'HIGH',
        timestamp: Date.now(),
        emf_anomaly_score: 0.05,
        detection_state: 'AMBIENT_EARTH_FIELD'
      },
      usb: {
        status: 'DISCONNECTED',
        device_count: 0,
        baud_rate: 115200
      }
    }
  };
}

/**
 * Starts the Project Bastet foreground service daemon
 */
export async function startBastetService(): Promise<{ success: boolean; message: string }> {
  if (isBastetNativeSupported()) {
    try {
      return await BastetNative.startService();
    } catch (err: any) {
      return { success: false, message: err.message };
    }
  }
  return { success: true, message: 'Web preview: Bastet simulated service started.' };
}

/**
 * Stops the Project Bastet foreground service daemon
 */
export async function stopBastetService(): Promise<{ success: boolean; message: string }> {
  if (isBastetNativeSupported()) {
    try {
      return await BastetNative.stopService();
    } catch (err: any) {
      return { success: false, message: err.message };
    }
  }
  return { success: true, message: 'Web preview: Bastet simulated service stopped.' };
}

/**
 * Returns instantaneous magnetic flux density (µT) and EMF anomaly detection
 */
export async function readMagnetometer(): Promise<MagnetometerData> {
  if (isBastetNativeSupported()) {
    try {
      return await BastetNative.readMagnetometer();
    } catch (err: any) {
      console.warn('[BastetBridge] readMagnetometer error:', err);
    }
  }

  // Loopback HTTP fallback
  try {
    const res = await fetch('http://127.0.0.1:8777/sensors/magnetometer', {
      method: 'GET',
      headers: { Accept: 'application/json' },
      signal: AbortSignal.timeout(1000)
    });
    if (res.ok) {
      return await res.json();
    }
  } catch {}

  // Web fallback simulation
  const baseMag = 46.0 + (Math.random() * 2.0 - 1.0);
  return {
    x_uT: 14.0 + (Math.random() * 0.5),
    y_uT: -38.0 + (Math.random() * 0.5),
    z_uT: -22.0 + (Math.random() * 0.5),
    magnitude_uT: Math.round(baseMag * 100) / 100,
    accuracy: 'HIGH',
    timestamp: Date.now(),
    emf_anomaly_score: 0.05,
    detection_state: 'AMBIENT_EARTH_FIELD'
  };
}

/**
 * Captures comprehensive diagnostic snapshot (magnetometer, ambient light, thermals, battery)
 */
export async function getDiagnosticSnapshot(): Promise<BastetDiagnosticSnapshot> {
  if (isBastetNativeSupported()) {
    try {
      return await BastetNative.getSensorSnapshot();
    } catch (err: any) {
      console.warn('[BastetBridge] getSensorSnapshot error:', err);
    }
  }

  // Loopback HTTP fallback
  try {
    const res = await fetch('http://127.0.0.1:8777/sensors/snapshot', {
      method: 'GET',
      headers: { Accept: 'application/json' },
      signal: AbortSignal.timeout(1000)
    });
    if (res.ok) {
      return await res.json();
    }
  } catch {}

  // Web fallback simulation
  return {
    timestamp: Date.now(),
    magnetometer: await readMagnetometer(),
    ambient_light: {
      available: true,
      lux: 340.5,
      timestamp: Date.now()
    },
    ambient_temperature: {
      available: false,
      celsius: null
    },
    battery: {
      level_percent: 88,
      temperature_celsius: 31.4,
      voltage_mv: 4120,
      status: 'CHARGING',
      is_charging: true,
      power_source: 'USB_OTG_HOST',
      health: 'GOOD',
      current_ma: -450
    }
  };
}

/**
 * Polls for an NFC transponder entering the RF field
 */
export async function pollNfcTag(timeoutMs: number = 3000): Promise<{ tagFound: boolean; tag?: NfcTagInfo; message?: string }> {
  if (isBastetNativeSupported()) {
    try {
      return await BastetNative.pollNfc({ timeoutMs });
    } catch (err: any) {
      return { tagFound: false, message: err.message };
    }
  }

  // Loopback JSON-RPC fallback
  try {
    const res = await fetch('http://127.0.0.1:8777/rpc', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ jsonrpc: '2.0', id: 1, method: 'nfc.poll', params: { timeout_ms: timeoutMs } }),
      signal: AbortSignal.timeout(timeoutMs + 1000)
    });
    if (res.ok) {
      const data = await res.json();
      if (data.result) return data.result;
    }
  } catch {}

  return {
    tagFound: false,
    message: 'NFC interrogation requires native Android hardware with ReaderMode enabled.'
  };
}

/**
 * Transmits payload over USB serial with framing and profile options
 */
export async function sendUsbTxRx(options: {
  port?: number;
  payload: string;
  timeoutMs?: number;
  baudRate?: number;
  framing?: 'raw' | 'line' | 'json_lines' | 'cobs';
  profile?: 'generic' | 'esp32_console' | 'logic_sniffer' | 'rp2040_picoprobe' | 'bus_pirate';
}): Promise<UsbTxRxResult> {
  if (isBastetNativeSupported()) {
    try {
      return await BastetNative.serialTxRx(options);
    } catch (err: any) {
      return {
        success: false,
        transmitted: options.payload,
        response: '',
        bytes_sent: 0,
        bytes_received: 0,
        duration_ms: 0,
        framing: options.framing || 'line',
        profile: options.profile || 'generic',
        error: err.message
      };
    }
  }

  // Loopback JSON-RPC fallback
  try {
    const res = await fetch('http://127.0.0.1:8777/rpc', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({
        jsonrpc: '2.0',
        id: Date.now(),
        method: 'usb.serial_tx_rx',
        params: {
          port: options.port || 0,
          payload: options.payload,
          timeout_ms: options.timeoutMs || 2000,
          baud_rate: options.baudRate || 0, // 0 = keep the open session's current rate
          framing: options.framing || 'line',
          profile: options.profile || 'generic'
        }
      }),
      signal: AbortSignal.timeout((options.timeoutMs || 2000) + 1500)
    });
    if (res.ok) {
      const data = await res.json();
      if (data.result) return data.result;
    }
  } catch {}

  return {
    success: false,
    transmitted: options.payload,
    response: '',
    bytes_sent: 0,
    bytes_received: 0,
    duration_ms: 0,
    framing: options.framing || 'line',
    profile: options.profile || 'generic',
    error: 'USB OTG multiplexer accessible when running on Android hardware.'
  };
}

/**
 * Scans nearby BLE devices and interrogates bonded Bluetooth hardware
 */
export async function scanBluetoothDevices(timeoutMs: number = 2500): Promise<BluetoothScanResult> {
  if (isBastetNativeSupported()) {
    try {
      return await BastetNative.scanBluetooth({ timeoutMs });
    } catch (err: any) {
      console.warn('[BastetBridge] scanBluetooth error:', err);
    }
  }

  // Loopback JSON-RPC / HTTP fallback (:8777)
  try {
    const res = await fetch('http://127.0.0.1:8777/rf/bluetooth/scan', {
      method: 'GET',
      headers: { Accept: 'application/json' },
      signal: AbortSignal.timeout(timeoutMs + 2000)
    });
    if (res.ok) {
      return await res.json();
    }
  } catch {}

  return {
    success: false,
    adapterState: 'OFF',
    adapterEnabled: false,
    totalDevices: 0,
    devices: [],
    error: 'Bluetooth scanning requires native Android hardware.'
  };
}

/**
 * Scans 2.4 GHz, 5 GHz, and 6 GHz Wi-Fi airspace and channel congestion
 */
export async function scanWifiAirspace(): Promise<WifiAirspaceResult> {
  if (isBastetNativeSupported()) {
    try {
      return await BastetNative.scanWifi();
    } catch (err: any) {
      console.warn('[BastetBridge] scanWifi error:', err);
    }
  }

  // Loopback HTTP fallback (:8777)
  try {
    const res = await fetch('http://127.0.0.1:8777/rf/wifi/scan', {
      method: 'GET',
      headers: { Accept: 'application/json' },
      signal: AbortSignal.timeout(4000)
    });
    if (res.ok) {
      return await res.json();
    }
  } catch {}

  return {
    success: false,
    measurementState: 'UNAVAILABLE',
    totalAccessPoints: 0,
    accessPoints: [],
    error: 'Wi-Fi airspace radar requires native Android hardware.'
  };
}

/**
 * Retrieves cellular carrier and serving cell signal telemetry
 */
export async function getCellularInfo(): Promise<any> {
  if (isBastetNativeSupported()) {
    try {
      return await BastetNative.getCellularInfo();
    } catch (err: any) {
      console.warn('[BastetBridge] getCellularInfo error:', err);
    }
  }

  try {
    const res = await fetch('http://127.0.0.1:8777/rf/cell/info', {
      method: 'GET',
      headers: { Accept: 'application/json' },
      signal: AbortSignal.timeout(2000)
    });
    if (res.ok) {
      return await res.json();
    }
  } catch {}

  return { error: 'Cellular telemetry requires native Android hardware.' };
}

/**
 * Disables Wi-Fi scan throttling via Shizuku
 */
export async function optimizeWifiScanThrottling(): Promise<{ success: boolean }> {
  if (isBastetNativeSupported()) {
    try {
      return await BastetNative.optimizeScanThrottling();
    } catch (err: any) {
      console.warn('[BastetBridge] optimizeScanThrottling error:', err);
    }
  }

  try {
    const res = await fetch('http://127.0.0.1:8777/api/rf/wifi/throttle/disable', {
      method: 'POST',
      headers: { Accept: 'application/json' },
      signal: AbortSignal.timeout(2000)
    });
    if (res.ok) {
      return await res.json();
    }
  } catch {}

  return { success: false };
}

